import React from 'react';

import Table from '../../Shared/UI/Table/components/Table';
import Thead from '../../Shared/UI/Table/components/Thead';
import Tbody from '../../Shared/UI/Table/components/Tbody';
import Tr from '../../Shared/UI/Table/components/Tr';
import Th from '../../Shared/UI/Table/components/Th';
import Td from '../../Shared/UI/Table/components/Td';
import Button from '../../Shared/UI/Button/components/Button';
import ActionRow from '../../Shared/UI/ActionRow/components/ActionRow';
import Accordion from '../../Shared/UI/Accordion/components/Accordion';
import Checkbox from '../../Shared/UI/Checkbox/components/Checkbox';
import Dropdown from '../../Shared/UI/Dropdown/components/Dropdown';

import { useGroupJobProspectsState, useGroupJobProspectsActions } from './GroupJobProspectsContext';
import { approveJobProspect, rejectJobProspect, unrejectJobProspect, bulkUpdateJobProspects, superAdminApproveJobProspect, superAdminRejectJobProspect, superAdminBulkUpdateJobProspects, archiveJobProspect, bulkArchiveJobProspects } from './restApi';
import styles from './GroupJobProspects.module.scss';

const statusLabels = {
  awaiting_super_admin_approval: 'Awaiting Super Admin Approval',
  rejected_by_super_admin: 'Rejected By Super Admin',
  awaiting_admin_approval: 'Awaiting Admin Approval',
  rejected_by_admin: 'Rejected By Admin',
  not_started: 'Not Started',
  application_in_process: 'Application In Process',
  applied_and_waiting: 'Applied And Waiting',
  interviewing: 'Interviewing',
  offer_received: 'Offer Received',
  offer_accepted: 'Offer Accepted',
  not_hiring: 'Not Hiring',
  no_offer: 'No Offer',
  decided_against: 'Decided Against',
  waiting_for_decision: 'Waiting For Decision',
  closed: 'Closed',
};

const SortIndicator = ({ column, sortBy, sortDirection }) => {
  if (sortBy !== column) return <span className={styles.sortIndicator}>↕</span>;
  return <span className={styles.sortIndicator}>{sortDirection === 'desc' ? '↓' : '↑'}</span>;
};

const GroupJobProspectsTable = () => {
  const state = useGroupJobProspectsState();
  const { onResponse, onError, openJobClosedWarningModal, openGenerateResumeModal, setSortParams, toggleJobProspectSelection, selectAllJobProspects, clearSelection } = useGroupJobProspectsActions();
  const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
  const generatingJobProspectIds = state?.generatingJobProspectIds || [];

  const handleSort = (column) => {
    const newDirection = state?.sortBy === column && state?.sortDirection === 'asc' ? 'desc' : 'asc';
    setSortParams(column, newDirection);
  };

  const sortJobProspects = (jobProspects) => {
    const { sortBy, sortDirection } = state || {};
    if (!sortBy) return jobProspects;

    return [...jobProspects].sort((a, b) => {
      const valA = (a[sortBy] || '').toLowerCase();
      const valB = (b[sortBy] || '').toLowerCase();
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleApproveJobProspect = (jobProspect) => {
    approveJobProspect({
      groupId: state?.group?.id,
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleRejectJobProspect = (jobProspect) => {
    rejectJobProspect({
      groupId: state?.group?.id,
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleUnrejectJobProspect = (jobProspect) => {
    unrejectJobProspect({
      groupId: state?.group?.id,
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const selectedJobProspectIds = state?.selectedJobProspectIds || [];

  const handleBulkAction = (action) => {
    bulkUpdateJobProspects({
      groupId: state?.group?.id,
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectIds: selectedJobProspectIds,
        bulkAction: action,
      },
    });
  };

  const handleSuperAdminApproveJobProspect = (jobProspect) => {
    superAdminApproveJobProspect({
      groupId: state?.group?.id,
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleSuperAdminRejectJobProspect = (jobProspect) => {
    superAdminRejectJobProspect({
      groupId: state?.group?.id,
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleSuperAdminBulkAction = (action) => {
    superAdminBulkUpdateJobProspects({
      groupId: state?.group?.id,
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectIds: selectedJobProspectIds,
        bulkAction: action,
      },
    });
  };

  const archivableStatuses = ['rejected_by_admin', 'rejected_by_super_admin', 'closed', 'no_offer', 'decided_against', 'not_hiring'];

  const handleArchiveJobProspect = (jobProspect) => {
    archiveJobProspect({
      groupId: state?.group?.id,
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleBulkArchiveJobProspects = () => {
    bulkArchiveJobProspects({
      groupId: state?.group?.id,
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectIds: selectedJobProspectIds,
      },
    });
  };

  const renderResumeCell = (jobProspect) => {
    if (generatingJobProspectIds.includes(jobProspect.id)) {
      return <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Generating…</span>;
    }

    const resumeState = jobProspect.resumeGenerationState;

    if (resumeState === 'generated') {
      return <a href={`/resumes/${jobProspect.resumeId}?from=/groups/${state?.group?.id}/job_prospects`}>View Resume</a>;
    }

    if (resumeState === 'generating' || resumeState === 'pending') {
      return <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Generating…</span>;
    }

    if (resumeState === 'failed') {
      return <Button variant="secondary" size="sm" onClick={() => openGenerateResumeModal(jobProspect)}>Retry</Button>;
    }

    return <Button variant="secondary" size="sm" onClick={() => openGenerateResumeModal(jobProspect)}>Generate Resume</Button>;
  };

  const handleJobProspectLinkClick = (e, jobProspect) => {
    const isJobDescriptionClosed = jobProspect?.jobDescriptionClosedAt != null;

    if (isJobDescriptionClosed) {
      e.preventDefault();
      openJobClosedWarningModal(jobProspect);
    }
    // If not closed, let the default link behavior happen
  };

  const renderStatusGroup = (jobProspectStatusGroup) => {
    const label = statusLabels[jobProspectStatusGroup?.status] || jobProspectStatusGroup?.status;
    const count = jobProspectStatusGroup.jobProspects.length;
    const canManage = state?.group?.canManageGroupJobProspects;
    const canManageAll = state?.group?.canManageAllJobProspects;
    const status = jobProspectStatusGroup?.status;
    const isAwaitingGroup = status === 'awaiting_admin_approval';
    const isAwaitingSuperAdmin = status === 'awaiting_super_admin_approval';
    const isArchivable = archivableStatuses.includes(status);
    const showResumeColumn = status === 'not_started' || status === 'application_in_process';
    const showCheckboxes = (isAwaitingGroup && canManage) || (isAwaitingSuperAdmin && canManageAll) || (isArchivable && canManageAll);

    const sortedProspects = sortJobProspects(jobProspectStatusGroup.jobProspects);
    const allIds = sortedProspects.map(jp => jp.id);
    const allSelected = showCheckboxes && allIds.length > 0 && allIds.every(id => selectedJobProspectIds.includes(id));
    const someSelected = showCheckboxes && selectedJobProspectIds.length > 0;

    const handleSelectAll = () => {
      if (allSelected) {
        clearSelection();
      } else {
        selectAllJobProspects(allIds);
      }
    };

    const bulkHandler = isAwaitingSuperAdmin ? handleSuperAdminBulkAction : handleBulkAction;
    const hasActionsColumn = canManage || canManageAll;

    return (
      <Accordion
        key={status}
        title={`${label} (${count})`}
      >
        {showCheckboxes && someSelected && (
          <div className={styles.bulkActionBar}>
            <span>{selectedJobProspectIds.length} selected</span>
            <ActionRow>
              {isArchivable ? (
                <Button onClick={() => handleBulkArchiveJobProspects()}>
                  Archive Selected
                </Button>
              ) : (
                <>
                  <Button onClick={() => bulkHandler('approve')}>
                    Approve Selected
                  </Button>
                  <Button onClick={() => bulkHandler('reject')}>
                    Reject Selected
                  </Button>
                </>
              )}
            </ActionRow>
          </div>
        )}
        <Table>
          <Thead>
            <Tr>
              {showCheckboxes && (
                <Th>
                  <Checkbox checked={allSelected} onChange={handleSelectAll} />
                </Th>
              )}
              <Th>
                <span className={styles.sortableHeader} onClick={() => handleSort('candidateName')}>
                  Candidate <SortIndicator column="candidateName" sortBy={state?.sortBy} sortDirection={state?.sortDirection} />
                </span>
              </Th>
              <Th>
                <span className={styles.sortableHeader} onClick={() => handleSort('biopharmaGroupName')}>
                  Organization <SortIndicator column="biopharmaGroupName" sortBy={state?.sortBy} sortDirection={state?.sortDirection} />
                </span>
              </Th>
              <Th>
                <span className={styles.sortableHeader} onClick={() => handleSort('title')}>
                  Job Description <SortIndicator column="title" sortBy={state?.sortBy} sortDirection={state?.sortDirection} />
                </span>
              </Th>
              <Th>Added By</Th>
              <Th>Interest Level</Th>
              {showResumeColumn && <Th>Resume</Th>}
              {hasActionsColumn && <Th />}
            </Tr>
          </Thead>
          <Tbody>
            {sortedProspects.map(jobProspect => {
              const jpStatus = jobProspect?.status;
              const isAwaitingApproval = jpStatus === 'awaiting_admin_approval';
              const isRejected = jpStatus === 'rejected_by_admin';
              const isAwaitingSuperAdminApproval = jpStatus === 'awaiting_super_admin_approval';
              const isArchivableStatus = archivableStatuses.includes(jpStatus);
              const hasGroupAdminActions = canManage && (isAwaitingApproval || isRejected);
              const hasSuperAdminActions = canManageAll && isAwaitingSuperAdminApproval;
              const hasArchiveAction = isArchivableStatus && canManageAll;
              const isJobDescriptionClosed = jobProspect?.jobDescriptionClosedAt != null;
              const isSelected = selectedJobProspectIds.includes(jobProspect.id);

              return (
                <Tr key={jobProspect.id} style={isJobDescriptionClosed ? { backgroundColor: 'var(--color-bg-subtle)' } : {}}>
                  {showCheckboxes && (
                    <Td>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleJobProspectSelection(jobProspect.id)}
                      />
                    </Td>
                  )}
                  <Td>
                    {jobProspect.candidatePath && jobProspect.candidateName
                      ? <a href={jobProspect.candidatePath} target="_blank" rel="noopener noreferrer">{jobProspect.candidateName}</a>
                      : null
                    }
                  </Td>
                  <Td>
                    <a href={jobProspect.biopharmaGroupLink} target="_blank" rel="noopener noreferrer">{jobProspect.biopharmaGroupName}</a>
                  </Td>
                  <Td>
                    <a
                      href={jobProspect.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => handleJobProspectLinkClick(e, jobProspect)}
                    >
                      {jobProspect.title}
                    </a>
                    <div style={{ fontSize: '0.85em', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                      {jobProspect.location && jobProspect.location !== 'undetermined' && jobProspect.location !== 'unknown'
                        ? jobProspect.location
                        : 'Location: pending'}
                    </div>
                  </Td>
                  <Td>{jobProspect.addedByName}</Td>
                  <Td>{jobProspect?.candidateInterestLevel}</Td>
                  {showResumeColumn && (
                    <Td>
                      {renderResumeCell(jobProspect)}
                    </Td>
                  )}
                  {hasActionsColumn && (
                    <Td>
                      {(hasGroupAdminActions || hasSuperAdminActions || hasArchiveAction) && (
                        <Dropdown
                          trigger={<Button variant="secondary" size="sm">Actions</Button>}
                          align="right"
                        >
                          {isAwaitingApproval && canManage && (
                            <>
                              <Dropdown.Item onClick={() => handleApproveJobProspect(jobProspect)}>
                                Approve
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleRejectJobProspect(jobProspect)}>
                                Reject
                              </Dropdown.Item>
                            </>
                          )}
                          {isRejected && canManage && (
                            <Dropdown.Item onClick={() => handleUnrejectJobProspect(jobProspect)}>
                              Move to Awaiting Approval
                            </Dropdown.Item>
                          )}
                          {isAwaitingSuperAdminApproval && canManageAll && (
                            <>
                              <Dropdown.Item onClick={() => handleSuperAdminApproveJobProspect(jobProspect)}>
                                Approve for Admin Review
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleSuperAdminRejectJobProspect(jobProspect)}>
                                Reject
                              </Dropdown.Item>
                            </>
                          )}
                          {hasArchiveAction && (
                            <Dropdown.Item onClick={() => handleArchiveJobProspect(jobProspect)}>
                              Archive
                            </Dropdown.Item>
                          )}
                        </Dropdown>
                      )}
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Accordion>
    );
  };

  const jobProspectStatusGroups = state?.group?.jobProspectStatusGroups || [];
  const hasJobProspects = jobProspectStatusGroups.some(
    group => group?.jobProspects && group.jobProspects.length > 0
  );

  if (!hasJobProspects) {
    return (
      <p style={{ textAlign: 'center', fontWeight: 'bold' }}>No active job prospects available</p>
    );
  }

  return (
    <>
      {jobProspectStatusGroups.map((jobProspectStatusGroup) =>
        renderStatusGroup(jobProspectStatusGroup)
      )}
    </>
  );
};

export default GroupJobProspectsTable;
